import ElevatedPaper from "@/components/ElevatedPaper";
import Flex from "@/components/Flex";
import PageLayout from "@/components/PageLayout";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { PROVIDER_PATHS } from "@/constants/paths";
import useDisclosure from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useCreateGroupVisit } from "@/queries/group-visit/use-create-group-visit";
import { useUpdateGroupVisit } from "@/queries/group-visit/use-update-group-visit";
import { useUpdateGroupVisitArchive } from "@/queries/group-visit/use-update-group-visit-archive";
import { useUpdateGroupVisitStatus } from "@/queries/group-visit/use-update-group-visit-status";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import { GROUP_VISIT_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-visit-list";
import { useQueryClient } from "@tanstack/react-query";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GroupFormDrawer from "./GroupFormDrawer";
import GroupList from "./components/GroupList";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import {
  GROUP_CONFIRMATION,
  GROUP_FORM_MODE,
  GROUP_LIST_LABEL,
  GROUP_MESSAGE,
  GROUP_ROW_ACTION,
  GROUP_STATUS,
  GROUP_SUBMIT_ACTION,
} from "./group-visit-constant";
import {
  GroupDetail,
  GroupPayload,
  GroupRowAction,
  GroupSubmitAction,
} from "./group-visit-types";
import {
  getCreatedGroupId,
  getGroupDetailPath,
  getGroupEditPath,
  getGroupViewPath,
  getToggledGroupStatus,
} from "./group-visit-utils";
import { useGroupList } from "./hooks/use-group-list";

/**
 * @author Rajkumar Rathod
 */
const GroupVisitLayout = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const formDisclosure = useDisclosure();
  const archiveDisclosure = useDisclosure();
  const inactivateDisclosure = useDisclosure();
  const [actionGroup, setActionGroup] = useState<GroupDetail | undefined>();

  const {
    groups,
    totalCount,
    isLoading,
    searchKey,
    setSearchKey,
    statusFilter,
    setStatusFilter,
    pagination,
    setPagination,
    sortModel,
    setSortModel,
    existingGroupNames,
  } = useGroupList();

  const { mutateAsync: createGroupVisit } = useCreateGroupVisit();
  const { mutateAsync: updateGroupVisit } = useUpdateGroupVisit();
  const { mutateAsync: updateGroupVisitArchive } = useUpdateGroupVisitArchive();
  const { mutateAsync: updateGroupVisitStatus } = useUpdateGroupVisitStatus();

  const refreshGroupList = () =>
    queryClient.invalidateQueries({ queryKey: [GROUP_VISIT_LIST_QUERY_KEY] });

  const selectedGroup = formDisclosure.data as GroupDetail | undefined;
  const isEditMode = !!selectedGroup;

  const openGroup = (group?: GroupDetail) => formDisclosure.open(group);

  const openEditScreen = (group: GroupDetail) => {
    if (group.uuid) navigate(getGroupEditPath(group.uuid));
  };

  const openDetailScreen = (group: GroupDetail) => {
    if (group.uuid) navigate(getGroupDetailPath(group.uuid));
  };

  const openViewScreen = (group: GroupDetail) => {
    if (group.uuid) navigate(getGroupViewPath(group.uuid));
  };

  const handleRowAction = (action: GroupRowAction, group: GroupDetail) => {
    if (action === GROUP_ROW_ACTION.VIEW_DETAILS) {
      openViewScreen(group);
      return;
    }
    if (action === GROUP_ROW_ACTION.VIEW) {
      openDetailScreen(group);
      return;
    }
    if (action === GROUP_ROW_ACTION.EDIT) {
      openEditScreen(group);
      return;
    }
    if (action === GROUP_ROW_ACTION.CHANGE_STATUS) {
      if (getToggledGroupStatus(group.status) === GROUP_STATUS.INACTIVE) {
        setActionGroup(group);
        inactivateDisclosure.open();
        return;
      }
      handleStatusChange(group);
      return;
    }
    setActionGroup(group);
    if (action === GROUP_ROW_ACTION.ARCHIVE) archiveDisclosure.open();
  };

  const handleArchive = async () => {
    if (!actionGroup?.uuid) return;

    try {
      const res = await updateGroupVisitArchive({
        groupVisitId: actionGroup.uuid,
        status: true,
      });

      refreshGroupList();
      showSnackbar(getSuccessSnackbar(res));
      archiveDisclosure.close();
      setActionGroup(undefined);
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    }
  };

  const handleStatusChange = async (group: GroupDetail): Promise<boolean> => {
    if (!group.uuid) return false;

    try {
      const res = await updateGroupVisitStatus({
        groupVisitId: group.uuid,
        groupStatus: getToggledGroupStatus(group.status),
      });

      refreshGroupList();
      showSnackbar(getSuccessSnackbar(res));
      return true;
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
      return false;
    }
  };

  const handleInactivate = async () => {
    if (!actionGroup) return;
    if (!(await handleStatusChange(actionGroup))) return;

    inactivateDisclosure.close();
    setActionGroup(undefined);
  };

  // Rethrows so the drawer stays open and the user can correct the form.
  const handleSubmit = async (
    payload: GroupPayload,
    action: GroupSubmitAction
  ) => {
    const groupVisit = {
      groupName: payload.groupName,
      groupType: payload.groupType ?? "",
      therapyCategory: payload.therapyCategory ?? "",
      groupDescription: payload.description ?? "",
      defaultSessionDuration: payload.sessionDurationMinutes ?? "",
    };

    try {
      const res = isEditMode
        ? await updateGroupVisit({
            groupVisit: { ...groupVisit, uuid: selectedGroup?.uuid },
          })
        : await createGroupVisit({ groupVisit });

      refreshGroupList();
      showSnackbar(
        isEditMode
          ? getSuccessSnackbar(res)
          : { type: "success", message: GROUP_MESSAGE.createSuccess }
      );

      const createdGroupId = getCreatedGroupId(res);
      if (
        !isEditMode &&
        action === GROUP_SUBMIT_ACTION.SAVE_AND_NEXT &&
        createdGroupId
      ) {
        navigate(getGroupEditPath(createdGroupId));
      }
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
      throw error;
    }
  };

  return (
    <ElevatedPaper>
      <PageLayout
        header={
          <Link
            to={PROVIDER_PATHS.PATIENT_LIST}
            style={{ textDecoration: "none", width: "fit-content" }}
          >
            <Flex alignItems="center" gap={0.5}>
              <ArrowBackIcon
                sx={{ fontSize: 14, color: CHART_COLORS.btnSecondaryText }}
              />
              <Text
                {...CHART_TYPOGRAPHY.body5}
                c={CHART_COLORS.btnSecondaryText}
              >
                {GROUP_LIST_LABEL.backToPatients}
              </Text>
            </Flex>
          </Link>
        }
      >
        <GroupList
          groups={groups}
          isLoading={isLoading}
          totalCount={totalCount}
          searchKey={searchKey}
          onSearch={setSearchKey}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          pagination={pagination}
          onPaginationChange={setPagination}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onCreateGroup={() => openGroup()}
          onRowAction={handleRowAction}
          onRowClick={openDetailScreen}
        />

        <ConfirmationModal
          disclosureHost={archiveDisclosure}
          title={GROUP_CONFIRMATION.archiveTitle}
          content={GROUP_CONFIRMATION.archiveContent}
          confirmationText={GROUP_CONFIRMATION.archiveConfirm}
          onConfirm={handleArchive}
        />

        <ConfirmationModal
          disclosureHost={inactivateDisclosure}
          title={GROUP_CONFIRMATION.inactivateTitle}
          content={GROUP_CONFIRMATION.inactivateContent}
          confirmationText={GROUP_CONFIRMATION.inactivateConfirm}
          onConfirm={handleInactivate}
        />

        {formDisclosure.opened && (
          <GroupFormDrawer
            opened={formDisclosure.opened}
            onClose={formDisclosure.close}
            mode={isEditMode ? GROUP_FORM_MODE.EDIT : GROUP_FORM_MODE.CREATE}
            group={selectedGroup}
            existingGroupNames={existingGroupNames}
            onSubmit={handleSubmit}
          />
        )}
      </PageLayout>
    </ElevatedPaper>
  );
};

export default GroupVisitLayout;
